import { useEffect, useState } from 'react';
import {
  Card,
  Select,
  Input,
  Button,
  Space,
  Alert,
  Tag,
  message,
  Typography,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import {
  listAppPrompts,
  upsertAppPrompts,
  type App,
  type AppPromptRow,
} from '@/api/apps';

const { Text } = Typography;

/**
 * AppPromptsEditor — per-App LLM prompt 覆盖 (app_prompts 表, 000013).
 *
 * 用途: 当多个 App 共用同一个 workflow_code (e.g. 前世占卜 + 港风 都用
 * WF_PAST_LIFE → PersonaGenWorkflow), 但想各自有不同 prompt 调性时, 在这里
 * 给单个 App 写 overlay. worker dbPromptLoader 优先级:
 *   app_prompts (本表) → workflow_templates.prompts → binary embed
 *
 * 留空某个 key = 删除该 overlay (回退 workflow 默认).
 *
 * 区别于 /workflows/:code 的 Prompts tab: 那个改的是 workflow_templates.prompts
 * (所有共用该 workflow 的 App 都受影响); 这个只影响当前 App.
 */

interface Props {
  app: App;
}

// workflow_code 候选 — 与 wizard 的 select 保持一致.
const WORKFLOW_OPTIONS = [
  { value: 'WF_CHARACTER_GEN', label: 'WF_CHARACTER_GEN (灵魂动物 animal schema)' },
  { value: 'WF_PAST_LIFE', label: 'WF_PAST_LIFE (persona — 前世/港风/身份类)' },
];

const PROMPT_KEYS = ['system', 'story'];

export function AppPromptsEditor({ app }: Props) {
  const defaultWf =
    (app.config?.default_workflow_code as string) || 'WF_CHARACTER_GEN';
  const [workflowCode, setWorkflowCode] = useState(defaultWf);
  const [rows, setRows] = useState<AppPromptRow[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await listAppPrompts(app.id);
      setRows(resp.prompts || []);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.id]);

  // 切 workflow_code 或 rows 变 → 重填 draft (当前 workflow 下的 overlay)
  useEffect(() => {
    const next: Record<string, string> = {};
    PROMPT_KEYS.forEach((k) => {
      const found = rows.find(
        (r) => r.workflow_code === workflowCode && r.prompt_key === k,
      );
      next[k] = found?.body || '';
    });
    setDraft(next);
  }, [workflowCode, rows]);

  const onSave = async () => {
    setSaving(true);
    try {
      const resp = await upsertAppPrompts(app.id, workflowCode, draft);
      setRows(resp.prompts || []);
      message.success('已保存 — worker 60s 内生效 (空字段=回退 workflow 默认)');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      loading={loading}
      extra={
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>
          保存 App 覆盖
        </Button>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="per-App LLM prompt 覆盖. 只影响当前 App; 留空 = 回退 workflow_templates 默认 / binary embed. 用于多个 App 共用一个 workflow_code 但要不同调性的场景."
      />

      <Space style={{ marginBottom: 16 }}>
        <Text>workflow_code:</Text>
        <Select
          value={workflowCode}
          onChange={setWorkflowCode}
          options={WORKFLOW_OPTIONS}
          style={{ width: 360 }}
        />
        {rows.some((r) => r.workflow_code === workflowCode) ? (
          <Tag color="green">本 App 有覆盖</Tag>
        ) : (
          <Tag>用 workflow 默认</Tag>
        )}
      </Space>

      {PROMPT_KEYS.map((k) => (
        <Card
          key={k}
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <Space>
              <Tag color="blue">{k}</Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {draft[k] ? `${draft[k].length} chars (本 App 覆盖)` : '(空 → 回退默认)'}
              </Text>
            </Space>
          }
        >
          <Input.TextArea
            value={draft[k]}
            onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
            autoSize={{ minRows: 6, maxRows: 24 }}
            style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}
            placeholder={`${k} — 留空则用 workflow_templates.prompts.${k} 或 binary embed`}
          />
        </Card>
      ))}
    </Card>
  );
}
